class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.initializeCartDrawer();

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('showCart') === 'true') {
      this.open(document.querySelector('#cart-icon-bubble'));
    }

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    const overlay = this.querySelector('#CartDrawer-Overlay .overlay-bg');
    if (overlay) {
      overlay.addEventListener('click', (evt) => {
        if (evt.target === overlay) {
          this.close();
        }
      });
    }

    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    // Removed cart update subscription since functionality moved to cart.js
  }

  disconnectedCallback() {
    // Removed unsubscriber since we removed the subscription
  }

  getCart() {
    return fetch(window.Shopify.routes.root + 'cart.js').then((res) => res.json());
  }

  initializeCartDrawer() {
    const cartSection = document.querySelector('section[id*="cart_drawer"]');
    if (cartSection?.id) {
      const match = cartSection.id.match(/sections--(.*)/);
      this.cartDrawerId = match ? match[1] : cartSection.id;
    } else {
      const cartDrawerParent = document.querySelector('[id^="CartDrawerParent"]');
      if (cartDrawerParent?.id) {
        this.cartDrawerId = cartDrawerParent.id.split('CartDrawerParent--')[1];
      }
    }

    if (!this.cartDrawerId && this.id) {
      this.cartDrawerId = this.id.split('CartDrawerParent--')[1];
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState, shouldOpen = true) {
    if (!parsedState?.sections) {
      try {
        const cart = await fetch(window.Shopify.routes.root + 'cart.js').then((res) => res.json());
        if (cart?.sections) {
          return this.renderContents(cart);
        }
      } catch (error) {
        console.error('Error fetching cart data:', error);
      }
      return;
    }

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;
    const sectionsToRender = this.getSectionsToRender();

    try {
      const cart = await fetch(window.Shopify.routes.root + 'cart.js').then((res) => res.json());
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        const cartCount = cartIconBubble.querySelector('.cart-count-bubble');
        if (cartCount) {
          cartCount.textContent = cart.item_count;
          cartCount.classList.toggle('hidden', cart.item_count === 0);
        }
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }

    sectionsToRender.forEach((section) => {
      if (!section.id || !parsedState.sections[section.id]) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      try {
        const sectionHtml = parsedState.sections[section.id];
        if (!sectionHtml) return;

        // Parse the HTML to find the specific element we want to update
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');

        if (section.selector) {
          // Find the specific element in the new HTML
          const newContent = doc.querySelector(section.selector);
          if (newContent) {
            // Find the corresponding element in the current DOM
            const targetElement = document.querySelector(section.selector);
            if (targetElement) {
              targetElement.innerHTML = newContent.innerHTML;
            }
          }
        } else {
          // If no specific selector is provided, update the entire section
          sectionElement.innerHTML = sectionHtml;
        }
      } catch (error) {
        console.error(`Error updating section ${section.id}:`, error);
      }
    });

    requestAnimationFrame(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay .overlay-bg');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }

      if (shouldOpen) {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';

    try {
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
      const sectionContent = parsedHtml.querySelector(selector);
      return sectionContent?.innerHTML || '';
    } catch (error) {
      console.error('Error parsing HTML:', error);
      return '';
    }
  }

  getSectionsToRender() {
    if (!this.cartDrawerId) {
      this.initializeCartDrawer();
    }

    if (!this.cartDrawerId) {
      console.error('Could not determine cart drawer ID');
      return [];
    }

    const drawerParentElement = document.querySelector(`#CartDrawerParent--sections--${this.cartDrawerId}`);
    const selectorId = drawerParentElement ? `sections--${this.cartDrawerId}` : this.cartDrawerId;

    return [
      {
        id: selectorId,
        selector: '.cart-drawer__items',
        section: 'cart-drawer-items',
      },
      {
        id: selectorId,
        selector: '.cart-drawer__footer',
        section: 'cart-drawer-items',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Cart Drawer Items is in cart.js
